<template>
  <div class="shop-detail-page">
    <nav_list :index="4"></nav_list>

    <div class="detail-wrap">
      <div v-if="loading" class="state-card">
        <i class="el-icon-loading"></i>
        <span>商品详情加载中...</span>
      </div>

      <div v-else-if="!info.prod_no" class="state-card">
        <span>未找到商品详情</span>
      </div>

      <template v-else>
        <div class="breadcrumb-row">
          <el-page-header @back="jump('/shop')" content="商品详情"></el-page-header>
        </div>

        <div class="hero-card">
          <div class="hero-gallery">
            <el-carousel
              v-if="bannerImages.length"
              class="hero-carousel"
              :interval="5000"
              arrow="always"
              height="460px"
            >
              <el-carousel-item v-for="(src, index) in bannerImages" :key="index">
                <img :src="src" :alt="info.prod_name" class="hero-image">
              </el-carousel-item>
            </el-carousel>
          </div>

          <div class="hero-summary">
            <div class="hero-category">
              <span>{{ displayCategoryLabel }}</span>
            </div>
            <h1 class="hero-title">{{ info.prod_name }}</h1>

            <div v-if="productTags.length" class="tag-list">
              <span v-for="tag in productTags" :key="tag" class="tag-item">{{ tag }}</span>
            </div>

            <div class="price-row">
              <div class="price-box">
                <span class="price-prefix">¥</span>
                <span class="price-value">{{ info.b2c_min_price || 0 }}</span>
                <span class="price-unit">起</span>
              </div>
              <div v-if="info.avg_scores" class="score-box">
                <i class="el-icon-star-on"></i>
                <span>{{ info.avg_scores }}</span>
              </div>
            </div>

            <div class="summary-grid">
              <div class="summary-item" v-if="info.days">
                <span class="label">行程天数</span>
                <span class="value">{{ info.days }} 天</span>
              </div>
              <div class="summary-item" v-if="info.hours || info.minute">
                <span class="label">体验时长</span>
                <span class="value">{{ formatDuration(info.hours, info.minute) }}</span>
              </div>
            </div>

            <div class="action-row">
              <el-button type="primary" size="medium" @click="openPackageDialog">立即购买</el-button>
              <el-button plain size="medium" @click="toggleFavorite">
                {{ info.is_favorited ? '取消收藏' : '加入收藏' }}
              </el-button>
            </div>

            <div class="package-brief" v-if="packages.length">
              <div class="brief-title">可选套餐</div>
              <div class="brief-list">
                <div v-for="pkg in packages.slice(0, 3)" :key="pkg.pkg_no" class="brief-item">
                  <span class="name">{{ pkg.pkg_name }}</span>
                  <span class="price">¥{{ pkg.b2c_min_price || 0 }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="navSections.length && showQuickNav" class="quick-nav">
          <div
            v-for="section in navSections"
            :key="section.id"
            class="quick-nav-item"
            :class="{ active: activeSection === section.id }"
            @click="scrollToSection(section.id)"
          >
            {{ section.name }}
          </div>
        </div>

        <div class="content-layout">
          <div class="content-main">
            <section v-if="info.introduction" id="section-features" class="content-card">
              <div class="section-title">商品特色</div>
              <div class="rich-block" v-html="info.introduction"></div>
            </section>

            <section v-if="includeExcludeData" id="section-include" class="content-card">
              <div class="section-title">费用说明</div>
              <div class="double-panel">
                <div class="double-item">
                  <div class="mini-title include">费用包含</div>
                  <ul class="bullet-list">
                    <li v-for="(item, index) in includeExcludeData.include" :key="'include-' + index">
                      {{ getDesc(item) }}
                    </li>
                  </ul>
                </div>
                <div class="double-item">
                  <div class="mini-title exclude">费用不包含</div>
                  <ul class="bullet-list">
                    <li v-for="(item, index) in includeExcludeData.exclude" :key="'exclude-' + index">
                      {{ getDesc(item) }}
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            <section v-if="exchangeData.length" id="section-usage" class="content-card">
              <div class="section-title">使用方式</div>
              <div class="info-list">
                <div v-for="item in exchangeData" :key="item.title" class="info-list-item">
                  <div class="item-label">{{ item.title }}</div>
                  <div class="item-value">{{ item.desc }}</div>
                </div>
              </div>
            </section>

            <section v-if="validityData" id="section-validity" class="content-card">
              <div class="section-title">使用期限</div>
              <div class="plain-text">{{ validityData.desc || '' }}</div>
            </section>

            <section v-if="refundData" id="section-refund" class="content-card">
              <div class="section-title">取消政策</div>
              <div class="notice-tip">请以套餐与下单界面为准</div>
            </section>

            <section v-if="noticeData" id="section-notice" class="content-card">
              <div class="section-title">注意事项</div>
              <ul class="bullet-list">
                <li v-for="(item, index) in noticeData.list" :key="'notice-' + index">
                  {{ getDesc(item) }}
                </li>
              </ul>
            </section>

            <section v-if="locationList.length" id="section-location" class="content-card">
              <div class="section-title">体验地点</div>
              <div class="location-list">
                <div v-for="(location, index) in locationList" :key="index" class="location-item">
                  <div class="location-head">
                    <div class="location-index">{{ index + 1 }}</div>
                    <div class="location-title">{{ getLocationDesc(location) || '未知地点' }}</div>
                  </div>
                  <div v-if="getLocationAddress(location)" class="location-text">
                    {{ getLocationAddress(location) }}
                  </div>
                  <div v-if="getArrivalDesc(location)" class="location-subblock">
                    <div class="sub-title">如何抵达</div>
                    <div class="rich-inline" v-html="getArrivalDesc(location)"></div>
                  </div>
                  <div v-if="getActiveTimeDesc(location)" class="location-subblock">
                    <div class="sub-title">开放时间说明</div>
                    <div class="rich-inline" v-html="getActiveTimeDesc(location)"></div>
                  </div>
                </div>
              </div>
            </section>

            <section v-if="hoursData" id="section-hours" class="content-card">
              <div class="section-title">开放时间</div>
              <div class="hours-table">
                <div class="hours-head">
                  <span>星期</span>
                  <span>开始时间</span>
                  <span>结束时间</span>
                  <span>最后入场</span>
                </div>
                <div v-for="(item, index) in hoursData.list" :key="index" class="hours-row">
                  <span>{{ getNestedDesc(item.week_title) }}</span>
                  <span>{{ getNestedDesc(item.start_time) }}</span>
                  <span>{{ getNestedDesc(item.end_time) }}</span>
                  <span>{{ getNestedDesc(item.last_admission) }}</span>
                </div>
              </div>
            </section>

            <section v-if="descriptionData" id="section-description" class="content-card">
              <div class="section-title">商品说明</div>
              <div class="rich-block" v-html="descriptionData.desc || ''"></div>
            </section>

            <section v-if="contentHtml" id="section-intro" class="content-card">
              <div class="section-title">详细介绍</div>
              <div class="rich-block" v-html="contentHtml"></div>
            </section>

            <section v-if="graphicData.length" id="section-graphic" class="content-card">
              <div class="section-title">图文介绍</div>
              <div class="graphic-list">
                <div v-for="(item, index) in graphicData" :key="index" class="graphic-item">
                  <img
                    :src="item.media[0].source_content"
                    :alt="item.desc || ('graphic-' + index)"
                    class="graphic-image"
                  >
                  <div class="graphic-desc">{{ item.desc || '' }}</div>
                </div>
              </div>
            </section>
          </div>

          <div class="content-side">
            <div class="side-card">
              <div class="side-title">购买信息</div>
              <div class="side-price">¥ {{ info.b2c_min_price || 0 }} <span>起</span></div>
              <div class="side-tips">
                <div v-if="info.instant_confirm">支持即时确认</div>
                <div v-if="info.is_cancel_free">部分套餐支持免费取消</div>
                <div v-if="packages.length">共 {{ packages.length }} 个套餐可选</div>
              </div>
              <el-button type="primary" style="width: 100%;" @click="openPackageDialog">立即选购</el-button>
            </div>
          </div>
        </div>

        <shop-purchase-dialog
          :visible.sync="packageDialogVisible"
          :packages="packages"
          :package-availability="packageAvailability"
          :current-pkg-no="currentPkgNo"
          :current-package="currentPackage"
          :current-package-loading="currentPackageLoading"
          :current-package-detail="currentPackageDetail"
          :current-package-unavailable-message="currentPackageUnavailableMessage"
          :current-item="currentItem"
          :selected-options="selectedOptions"
          :show-date-picker="showDatePicker"
          :date-picker-value="datePickerValue"
          :date-picker-type="datePickerType"
          :date-picker-options="datePickerOptions"
          :sale-start-date="formatSaleDate(currentPackageDetail && currentPackageDetail.sale_s_date)"
          :sale-end-date="formatSaleDate(currentPackageDetail && currentPackageDetail.sale_e_date)"
          :display-date-text="displayDateText"
          :has-time-slots="hasTimeSlots"
          :time-slots="timeSlots"
          :available-specs="availableSpecs"
          :last-spec-multi="lastSpecMulti"
          :selected-specs="selectedSpecs"
          :matched-skus="matchedSKUs"
          :total-quantity="totalQuantity"
          :total-price-b2-c="totalPriceB2C"
          :get-package-refund-desc="getPackageRefundDesc"
          :get-package-refund-lines="getPackageRefundLines"
          :get-time-slot-name="getTimeSlotName"
          :get-spec-item-price="getSpecItemPrice"
          :get-spec-item-quantity="getSpecItemQuantity"
          :is-spec-item-available-for-date="isSpecItemAvailableForDate"
          :get-sku-final-price-b2-c="getSkuFinalPriceB2C"
          :is-sku-available-for-date="isSkuAvailableForDate"
          @select-package="selectPackage"
          @date-input="handleDateInput"
          @select-time-slot="selectTimeSlot"
          @select-spec="selectSpec"
          @increase-spec-quantity="increaseSpecQuantity"
          @decrease-spec-quantity="decreaseSpecQuantity"
          @increase-sku-quantity="increaseSKUQuantity"
          @decrease-sku-quantity="decreaseSKUQuantity"
          @confirm="confirmPackageSelection"
        />
      </template>
    </div>
  </div>
</template>

<script>
import nav_list from '../index/nav_list.vue';
import ShopPurchaseDialog from './components/ShopPurchaseDialog.vue';
import shopPurchaseMixin from './mixins/shopPurchaseMixin';

export default {
  name: 'shop-detail',
  mixins: [shopPurchaseMixin],
  components: {
    nav_list,
    ShopPurchaseDialog,
  },
  data() {
    return {
      prodNo: '',
      loading: true,
      info: {},
      activeSection: '',
      scrollSections: [],
      showQuickNav: false,
    };
  },
  computed: {
    displayCategoryLabel() {
      return this.getDisplayText(
        this.info.category || this.info.cate_name || this.info.category_name,
        '商品详情',
      );
    },
    bannerImages() {
      if (!this.info) return [];
      if (this.info.img_list && this.info.img_list.length) return this.info.img_list;
      if (this.info.banners && this.info.banners.length) return this.info.banners;
      if (this.info.prod_img_url) {
        return Array.isArray(this.info.prod_img_url) ? this.info.prod_img_url : [this.info.prod_img_url];
      }
      return [];
    },
    productTags() {
      const tags = [];
      if (this.info.is_tour) tags.push('旅游');
      if (this.info.is_cancel_free) tags.push('免费取消');
      if (this.info.instant_confirm) tags.push('即时确认');
      if (this.info.instant_booking) tags.push('即时预订');
      return tags;
    },
    packages() {
      return this.info.packages || [];
    },
    includeExcludeData() {
      if (!this.info.pmdl_inc_ninc) return null;
      const props = this.getPmdlProperties(this.info.pmdl_inc_ninc);
      if (!props) return null;
      return {
        include: props.include_item && props.include_item.list ? props.include_item.list : [],
        exclude: props.not_include_item && props.not_include_item.list ? props.not_include_item.list : [],
      };
    },
    exchangeData() {
      if (!this.info.pmdl_exchange) return [];
      const props = this.getPmdlProperties(this.info.pmdl_exchange);
      if (!props) return [];
      return Object.keys(props)
        .filter((key) => key !== 'description')
        .map((key) => {
          const item = props[key] || {};
          return {
            title: item.title || key,
            desc: item.desc || '',
          };
        })
        .filter((item) => item.desc);
    },
    validityData() {
      if (!this.info.pmdl_use_valid) return null;
      const props = this.getPmdlProperties(this.info.pmdl_use_valid);
      return props && props.exchange ? props.exchange : null;
    },
    refundData() {
      if (!this.info.prod_no) return null;
      return {
        isDefault: true,
        defaultText: '请以套餐与下单界面为准',
      };
    },
    noticeData() {
      if (!this.info.pmdl_notice) return null;
      const props = this.getPmdlProperties(this.info.pmdl_notice);
      return props && props.cust_reminds ? props.cust_reminds : null;
    },
    locationList() {
      if (!this.info.pmdl_experience_location || !this.info.pmdl_experience_location.list) return [];
      return this.info.pmdl_experience_location.list;
    },
    hoursData() {
      if (!this.locationList.length) return null;
      const location = this.locationList[0];
      if (!location.active_time || !location.active_time.list || !location.active_time.list.length) return null;
      return location.active_time;
    },
    descriptionData() {
      const data = this.info.pmdl_introduce_summary;
      if (!data) return null;
      if (typeof data === 'object' && !Object.keys(data).length) return null;
      if (!data.desc || (typeof data.desc === 'string' && !data.desc.trim())) return null;
      return data;
    },
    contentHtml() {
      return this.info.pmdl_purchase_summary && this.info.pmdl_purchase_summary.desc
        ? this.info.pmdl_purchase_summary.desc
        : null;
    },
    graphicData() {
      if (!this.info.pmdl_graphic || !this.info.pmdl_graphic.list) return [];
      return this.info.pmdl_graphic.list.filter((item) => {
        return item && item.media && item.media[0] && item.media[0].source_content;
      });
    },
    navSections() {
      const sections = [];
      if (this.info.introduction) sections.push({ id: 'section-features', name: '商品特色' });
      if (this.includeExcludeData) sections.push({ id: 'section-include', name: '费用说明' });
      if (this.exchangeData.length) sections.push({ id: 'section-usage', name: '使用方式' });
      if (this.validityData) sections.push({ id: 'section-validity', name: '使用期限' });
      if (this.refundData) sections.push({ id: 'section-refund', name: '取消政策' });
      if (this.noticeData) sections.push({ id: 'section-notice', name: '注意事项' });
      if (this.locationList.length) sections.push({ id: 'section-location', name: '体验地点' });
      if (this.hoursData) sections.push({ id: 'section-hours', name: '开放时间' });
      if (this.descriptionData) sections.push({ id: 'section-description', name: '商品说明' });
      if (this.contentHtml) sections.push({ id: 'section-intro', name: '详细介绍' });
      if (this.graphicData.length) sections.push({ id: 'section-graphic', name: '图文介绍' });
      return sections;
    },
  },
  mounted() {
    this.prodNo = this.$route.query.prod_no || this.$route.query.id || '';
    if (!this.prodNo) {
      this.loading = false;
      this.$message.error('商品编号不能为空');
      return;
    }
    this.loadProductDetail();
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    window.addEventListener('resize', this.handleScroll, { passive: true });
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleScroll);
  },
  methods: {
    getDisplayText(value, fallback = '') {
      if (value == null) return fallback;
      if (typeof value === 'string' || typeof value === 'number') return String(value);
      if (Array.isArray(value)) {
        return value.map((item) => this.getDisplayText(item, '')).filter(Boolean).join(' / ') || fallback;
      }
      if (typeof value === 'object') {
        return this.getDisplayText(
          value.title || value.name || value.label || value.text || value.desc || value.value,
          fallback,
        );
      }
      return fallback;
    },
    async loadProductDetail() {
      this.loading = true;
      try {
        const res = await this.get('/kkday_product/detail/' + this.prodNo, {}, 'get', false);
        if (res && res.info) {
          this.info = res.info;
          this.prefetchPackageAvailability();
          this.$nextTick(() => {
            this.collectSections();
            this.handleScroll();
          });
        } else {
          this.$message.error('商品不存在或已下架');
        }
      } catch (error) {
        this.$message.error('加载商品详情失败');
      } finally {
        this.loading = false;
      }
    },
    getPmdlProperties(moduleData) {
      if (!moduleData) return null;
      if (moduleData.content && moduleData.content.properties) return moduleData.content.properties;
      if (moduleData.properties) return moduleData.properties;
      return moduleData;
    },
    getDesc(item) {
      if (!item) return '';
      return item.desc || item;
    },
    getNestedDesc(item) {
      if (!item) return '';
      return item.desc || '';
    },
    getLocationDesc(location) {
      return location && location.location_name ? (location.location_name.desc || location.location_name) : '';
    },
    getLocationAddress(location) {
      return location && location.latlng ? (location.latlng.desc || location.latlng) : '';
    },
    getArrivalDesc(location) {
      return location && location.arrival_desc ? (location.arrival_desc.desc || location.arrival_desc) : '';
    },
    getActiveTimeDesc(location) {
      return location && location.active_time_desc ? (location.active_time_desc.desc || location.active_time_desc) : '';
    },
    formatDuration(hours, minute) {
      const parts = [];
      if (hours) parts.push(hours + ' 小时');
      if (minute) parts.push(minute + ' 分钟');
      return parts.join(' ') || '-';
    },
    async toggleFavorite() {
      try {
        const res = await this.get('/kkday/favorites/toggle', {
          prod_no: this.prodNo,
        }, 'post', false);
        this.$set(this.info, 'is_favorited', !!res.is_favorited);
        this.$message.success(res.is_favorited ? '已加入收藏' : '已取消收藏');
      } catch (error) {
        this.$message.error('收藏操作失败');
      }
    },
    scrollToSection(id) {
      const element = document.getElementById(id);
      if (!element) return;
      const top = element.getBoundingClientRect().top + window.pageYOffset - 120;
      window.scrollTo({ top, behavior: 'smooth' });
      this.activeSection = id;
    },
    collectSections() {
      this.scrollSections = this.navSections
        .map((section) => {
          const element = document.getElementById(section.id);
          if (!element) return null;
          return {
            id: section.id,
            top: element.offsetTop,
          };
        })
        .filter(Boolean);
    },
    handleScroll() {
      this.showQuickNav = window.pageYOffset > Math.max(window.innerHeight - 180, 320);
      if (!this.scrollSections.length) return;
      const current = window.pageYOffset + 180;
      let active = this.scrollSections[0].id;
      this.scrollSections.forEach((section) => {
        if (current >= section.top) {
          active = section.id;
        }
      });
      this.activeSection = active;
    },
  },
};
</script>

<style scoped>
.shop-detail-page {
  min-height: 100vh;
  background: #f5f7fb;
  padding-bottom: 40px;
}

.detail-wrap {
  width: 1200px;
  margin: 20px auto 0;
}

.state-card,
.hero-card,
.content-card,
.side-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 16px 1px rgb(0 0 0 / 6%);
}

.state-card {
  min-height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.breadcrumb-row {
  margin-bottom: 16px;
}

.hero-card {
  display: flex;
  padding: 20px;
  gap: 24px;
}

.hero-gallery {
  width: 640px;
  flex-shrink: 0;
}

.hero-carousel {
  overflow: hidden;
  border-radius: 12px;
}

.hero-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.hero-summary {
  flex: 1;
  min-width: 0;
}

.hero-category span {
  display: inline-block;
  padding: 5px 12px;
  border-radius: 16px;
  background: #edf4ff;
  color: #1c5098;
  font-size: 12px;
}

.hero-title {
  margin: 14px 0 0;
  color: #222;
  font-size: 30px;
  line-height: 1.5;
}

.tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 18px;
}

.tag-item {
  padding: 6px 14px;
  border-radius: 20px;
  background: #f7f9fd;
  color: #666;
  font-size: 13px;
}

.price-row {
  display: flex;
  align-items: end;
  justify-content: space-between;
  margin-top: 24px;
  padding-bottom: 22px;
  border-bottom: 1px solid #edf1f7;
}

.price-box {
  color: #1c5098;
}

.price-prefix {
  font-size: 20px;
  font-weight: bold;
}

.price-value {
  font-size: 42px;
  font-weight: bold;
  line-height: 1;
}

.price-unit {
  margin-left: 4px;
  font-size: 14px;
  color: #999;
}

.score-box {
  color: #f59a23;
  font-size: 16px;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin-top: 20px;
}

.summary-item {
  padding: 14px 16px;
  background: #f8fafc;
  border-radius: 10px;
}

.summary-item .label {
  display: block;
  color: #8b95a7;
  font-size: 12px;
}

.summary-item .value {
  display: block;
  margin-top: 8px;
  color: #333;
  font-size: 15px;
  font-weight: bold;
}

.action-row {
  display: flex;
  gap: 12px;
  margin-top: 22px;
}

.package-brief {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #edf1f7;
}

.brief-title {
  color: #333;
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 12px;
}

.brief-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.brief-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-radius: 8px;
  background: #f8fafc;
}

.brief-item .name {
  color: #555;
  font-size: 14px;
}

.brief-item .price {
  color: #1c5098;
  font-size: 16px;
  font-weight: bold;
}

.quick-nav {
  position: fixed;
  top: 50%;
  right: max(16px, calc((100vw - 1200px) / 2));
  transform: translateY(-50%);
  z-index: 40;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 72px;
}

.quick-nav-item {
  position: relative;
  padding: 0 0 0 12px;
  color: #7b8798;
  cursor: pointer;
  font-size: 11px;
  line-height: 1.5;
  text-align: left;
  transition: all 0.2s ease;
}

.quick-nav-item::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  width: 3px;
  height: 3px;
  background: #b6c4d8;
  transform: translateY(-50%);
  transition: all 0.2s ease;
}

.quick-nav-item:hover {
  color: #1c5098;
  transform: translateX(-1px);
}

.quick-nav-item.active {
  color: #1c5098;
  font-weight: bold;
}

.quick-nav-item.active::before {
  width: 3px;
  height: 16px;
  background: #1c5098;
}

.content-layout {
  display: flex;
  gap: 20px;
  margin-top: 20px;
}

.content-main {
  flex: 1;
  min-width: 0;
}

.content-side {
  width: 280px;
  flex-shrink: 0;
}

.content-card {
  margin-bottom: 20px;
  padding: 24px;
}

.side-card {
  position: sticky;
  top: 84px;
  padding: 22px;
}

.side-title {
  color: #333;
  font-size: 18px;
  font-weight: bold;
}

.side-price {
  margin-top: 18px;
  color: #1c5098;
  font-size: 32px;
  font-weight: bold;
}

.side-price span {
  font-size: 14px;
  color: #999;
  font-weight: normal;
}

.side-tips {
  margin: 18px 0 22px;
  color: #666;
  font-size: 13px;
  line-height: 1.9;
}

.section-title {
  margin-bottom: 18px;
  color: #333;
  font-size: 22px;
  font-weight: bold;
}

.rich-block {
  color: #555;
  font-size: 14px;
  line-height: 1.9;
}

.double-panel {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
}

.double-item {
  padding: 18px;
  border-radius: 10px;
  background: #f8fafc;
}

.mini-title {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 12px;
}

.mini-title.include {
  color: #2d8a4f;
}

.mini-title.exclude {
  color: #c05b2b;
}

.bullet-list {
  margin: 0;
  padding-left: 18px;
  color: #555;
  line-height: 1.9;
}

.info-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.info-list-item {
  padding-bottom: 16px;
  border-bottom: 1px solid #edf1f7;
}

.info-list-item:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}

.item-label {
  color: #333;
  font-size: 15px;
  font-weight: bold;
}

.item-value,
.plain-text {
  margin-top: 8px;
  color: #555;
  font-size: 14px;
  line-height: 1.9;
}

.notice-tip {
  padding: 14px 16px;
  border-radius: 8px;
  background: #fff7ed;
  color: #c05b2b;
}

.location-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.location-item {
  padding: 18px;
  border-radius: 10px;
  background: #f8fafc;
}

.location-head {
  display: flex;
  align-items: center;
  gap: 12px;
}

.location-index {
  width: 28px;
  height: 28px;
  line-height: 28px;
  text-align: center;
  border-radius: 50%;
  background: #1c5098;
  color: #fff;
  font-size: 14px;
  font-weight: bold;
}

.location-title {
  color: #333;
  font-size: 16px;
  font-weight: bold;
}

.location-text {
  margin-top: 12px;
  color: #555;
  line-height: 1.8;
}

.location-subblock {
  margin-top: 14px;
}

.sub-title {
  color: #333;
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 8px;
}

.rich-inline {
  color: #555;
  line-height: 1.9;
}

.hours-table {
  border: 1px solid #edf1f7;
  border-radius: 10px;
  overflow: hidden;
}

.hours-head,
.hours-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
}

.hours-head {
  background: #f4f7fc;
  color: #666;
  font-size: 13px;
  font-weight: bold;
}

.hours-row {
  color: #555;
  font-size: 13px;
  border-top: 1px solid #edf1f7;
}

.hours-head span,
.hours-row span {
  padding: 12px 14px;
}

.graphic-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.graphic-item {
  overflow: hidden;
  border: 1px solid #edf1f7;
  border-radius: 12px;
}

.graphic-image {
  width: 100%;
  display: block;
}

.graphic-desc {
  padding: 16px;
  color: #555;
  line-height: 1.8;
}

@media (max-width: 1260px) {
  .quick-nav {
    right: 12px;
  }

  .detail-wrap {
    width: 96%;
  }

  .hero-card,
  .content-layout {
    display: block;
  }

  .hero-gallery,
  .content-side {
    width: 100%;
  }

  .hero-summary,
  .content-side {
    margin-top: 20px;
  }

  .side-card {
    position: static;
  }
}

@media (max-width: 992px) {
  .quick-nav {
    display: none;
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/shop/info.vue